import React, { useEffect, useState } from 'react';
import OwnerInlineText from './OwnerInlineText';

/**
 * Board Cover Overlay — cinematic full-screen entrance shown before the
 * client lands on the moodboard.
 *
 * Reads as a private invitation rather than a software loading screen.
 * Editorial three-stack layout: brand line → large Playfair title →
 * Cormorant italic subtitle → primary CTA → optional skip. Subtle fade
 * (~600ms ease-out) hands off to the Client View.
 *
 * Props:
 *   • board — the resolved client-side board payload. We read
 *     `board.board_cover_*` and `board.board_cover_image_url` (server-
 *     resolved). `board.name` falls back as the title when blank.
 *   • brandName — workspace name to show as the eyebrow.
 *   • brandLogoUrl — optional logo URL; rendered instead of the
 *     uppercase brand name when present.
 *   • onEnter — called when the client taps the CTA (or hits skip).
 *     Parent uses this to flip its state so the cover unmounts.
 *   • t — translation map; used for the default CTA label only.
 */
const BoardCoverOverlay = ({
  board,
  brandName = '',
  brandLogoUrl = '',
  onEnter,
  t = {},
  ownerEdit = null,
}) => {
  const [leaving, setLeaving] = useState(false);
  // When the logo image fails to load (404 / expired signed URL / CORS),
  // we silently fall back to the uppercase brand-name treatment so the
  // viewer never sees a broken-image icon on the cinematic cover.
  const [logoFailed, setLogoFailed] = useState(false);

  const title = (board?.board_cover_title || board?.name || '').trim();
  const subtitle = (board?.board_cover_subtitle || '').trim();
  const buttonText = (board?.board_cover_button_text || t.enter_board || 'Enter Board').trim();
  const showSkip = board?.board_cover_show_skip === true;
  const coverUrl = board?.board_cover_image_url || '';

  // Lock body scroll while the cover is visible. The client view sits
  // mounted underneath; we don't want a stray finger drag scrolling
  // through the moodboard before the client has tapped into it.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleEnter = () => {
    if (leaving) return;
    setLeaving(true);
    // 600ms matches the CSS fade-out below. Honest motion without ever
    // feeling slow — editorial "step through the curtain" rhythm.
    setTimeout(() => { onEnter?.(); }, 600);
  };

  return (
    <div
      data-testid="board-cover-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'var(--kahla-charcoal, #2A2724)',
        color: 'var(--kahla-cream, #F8F5EE)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        opacity: leaving ? 0 : 1,
        transition: 'opacity 0.6s ease-out',
      }}
    >
      {/* Background image — sits behind a dark veil so the typography
          stays legible across any cover photo. Slow Ken-Burns-style
          zoom-in keeps the page from feeling static during the moment
          the client takes to read the title. */}
      {coverUrl && (
        <>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${coverUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transform: leaving ? 'scale(1.05)' : 'scale(1.02)',
              transition: 'transform 8s ease-out',
              animation: 'cover-zoom 22s ease-out forwards',
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              // Dark editorial veil — gradient from slightly heavier at
              // the edges (vignette) to slightly lighter at center
              // for a private-presentation feel.
              background: 'radial-gradient(ellipse at center, rgba(20,18,16,0.45) 0%, rgba(20,18,16,0.75) 100%)',
            }}
          />
        </>
      )}

      {/* Foreground stack */}
      <div
        style={{
          position: 'relative',
          textAlign: 'center',
          padding: '24px',
          maxWidth: '960px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0',
        }}
      >
        {/* Brand line — logo when provided & loads, otherwise the
            uppercase Playfair brand name. A failed logo load (broken
            URL, expired signed URL, CORS) automatically falls back to
            the text treatment so the cover never shows a broken-image
            icon. */}
        {brandLogoUrl && !logoFailed ? (
          <img
            src={brandLogoUrl}
            alt={brandName || 'Brand'}
            data-testid="board-cover-brand-logo"
            onError={() => setLogoFailed(true)}
            style={{
              maxHeight: 'clamp(28px, 4vw, 40px)',
              maxWidth: '220px',
              objectFit: 'contain',
              marginBottom: 'clamp(36px, 6vw, 72px)',
              opacity: 0.95,
              filter: 'brightness(0) invert(1)',
            }}
          />
        ) : brandName ? (
          <p
            data-testid="board-cover-brand"
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(11px, 1.4vw, 13px)',
              letterSpacing: '0.38em',
              textTransform: 'uppercase',
              fontWeight: 500,
              opacity: 0.92,
              marginBottom: 'clamp(36px, 6vw, 72px)',
            }}
          >
            {brandName}
          </p>
        ) : null}

        {/* Title — large editorial display. */}
        <OwnerInlineText
          edit={!!ownerEdit?.active}
          as="h1"
          value={title}
          onSave={(v) => ownerEdit?.save({ board_cover_title: v })}
          placeholder={t.board_cover_title_placeholder || ' '}
          testId="board-cover-title"
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(36px, 7vw, 84px)',
            fontWeight: 500,
            letterSpacing: '0.005em',
            lineHeight: 1.08,
            margin: 0,
            color: 'inherit',
          }}
        />

        {/* Subtitle — Cormorant italic deck. */}
        {(subtitle || ownerEdit?.active) && (
          <OwnerInlineText
            edit={!!ownerEdit?.active}
            as="p"
            value={subtitle}
            onSave={(v) => ownerEdit?.save({ board_cover_subtitle: v })}
            placeholder={t.board_cover_subtitle_placeholder || ''}
            testId="board-cover-subtitle"
            style={{
              fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
              fontStyle: 'italic',
              fontSize: 'clamp(16px, 2.4vw, 22px)',
              letterSpacing: '0.04em',
              fontWeight: 400,
              lineHeight: 1.45,
              marginTop: 'clamp(14px, 1.8vw, 22px)',
              marginBottom: 0,
              opacity: 0.9,
              maxWidth: '640px',
            }}
          />
        )}

        {/* CTA — pill outline, tracking matches editorial eyebrow. In
            owner edit mode the button text itself is click-to-edit and
            a quiet "continue" link below steps through instead. */}
        <button
          type="button"
          onClick={ownerEdit?.active ? undefined : handleEnter}
          data-testid="board-cover-enter-btn"
          style={{
            marginTop: 'clamp(40px, 6vw, 72px)',
            padding: '14px 38px',
            background: 'transparent',
            border: '1px solid rgba(248, 245, 238, 0.6)',
            color: 'inherit',
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(11px, 1.4vw, 13px)',
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background-color 0.3s ease, border-color 0.3s ease',
            borderRadius: '999px',
            WebkitTapHighlightColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(248, 245, 238, 0.08)';
            e.currentTarget.style.borderColor = 'rgba(248, 245, 238, 1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(248, 245, 238, 0.6)';
          }}
        >
          {ownerEdit?.active ? (
            <OwnerInlineText
              edit
              value={board?.board_cover_button_text || ''}
              onSave={(v) => ownerEdit?.save({ board_cover_button_text: v })}
              placeholder={buttonText}
              testId="board-cover-button-text"
              style={{ color: 'inherit' }}
            />
          ) : buttonText}
          {' →'}
        </button>

        {ownerEdit?.active && (
          <button
            type="button"
            onClick={handleEnter}
            data-testid="board-cover-edit-continue"
            style={{
              marginTop: '22px',
              padding: '6px 12px',
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              opacity: 0.6,
              fontFamily: '"Playfair Display", serif',
              fontSize: '11px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'opacity 0.3s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.95'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
          >
            {t.cv_edit_continue_below || 'Continue to board ↓'}
          </button>
        )}

        {showSkip && (
          <button
            type="button"
            onClick={handleEnter}
            data-testid="board-cover-skip-btn"
            style={{
              marginTop: '20px',
              padding: '6px 12px',
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              opacity: 0.5,
              fontFamily: '"Playfair Display", serif',
              fontSize: '11px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'opacity 0.3s ease',
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
          >
            {t.skip_cover || 'Skip'}
          </button>
        )}
      </div>

      {/* Keyframes for the slow background zoom. Inlined so the component
          stays self-contained — no global stylesheet plumbing required. */}
      <style>{`
        @keyframes cover-zoom {
          from { transform: scale(1.02); }
          to   { transform: scale(1.10); }
        }
      `}</style>
    </div>
  );
};

export default BoardCoverOverlay;
